"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { cn } from "@/shared/lib/utils";
import type {
  HomeHowWeWorkStep,
  HomeHowWeWorkSectionContent,
} from "@/features/home/types/home-page-content";

interface HomeHowWeWorkSectionProps {
  content: HomeHowWeWorkSectionContent;
}

// Step 1 — top-left, curves down-right toward center
function Arrow1() {
  return (
    <svg height="64" viewBox="0 0 136 91" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clipPath="url(#arrow1-clip)">
        <path
          d="M83.2889 9.90207C110.968 23.256 126.026 53.6881 125.927 83.6643C126.11 83.8741 126.292 84.084 126.475 84.2939C127.181 74.0853 127.9 63.6804 128.802 53.4854C128.951 51.3259 132.485 51.5701 132.336 53.7296C131.521 65.5088 130.511 77.2743 129.697 89.0535C129.602 90.4277 127.571 91.2736 126.671 90.0279C126.122 89.3982 125.77 88.7821 125.222 88.1525C124.383 88.8835 123.008 88.7885 122.697 87.5834C121.486 85.1326 120.274 82.6818 118.867 80.2174C115.604 76.0468 112.342 71.8762 109.08 67.7056C107.631 65.8301 110.136 63.8335 111.808 65.3298C115.883 69.1621 119.132 73.529 121.778 78.0516C121.947 78.4578 122.13 78.6677 122.313 78.8775C121.03 51.7647 106.893 25.1442 81.9074 12.7654C56.347 0.149676 24.2104 5.62155 4.99486 26.7807C4.3652 27.329 3.43788 26.4759 3.87122 25.914C22.9854 3.36706 56.9431 -2.76797 83.2889 9.90207Z"
          fill="#4E7DA6"
        />
      </g>
      <defs>
        <clipPath id="arrow1-clip">
          <rect
            width="82.0603"
            height="130.076"
            fill="white"
            transform="matrix(0.0689514 -0.99762 -0.99762 -0.0689514 129.768 90.834)"
          />
        </clipPath>
      </defs>
    </svg>
  );
}

// Step 2 — top-right, curves down-left toward center
function Arrow2() {
  return (
    <svg height="60" viewBox="0 0 134 85" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clipPath="url(#arrow2-clip)">
        <path
          d="M117.592 81.7318C114.522 56.3157 102.803 27.4087 76.1217 19.2658C51.2623 11.7356 26.4583 24.2529 12.2942 44.6704C12.2539 44.8396 12.0846 44.7993 12.0443 44.9686C12.2216 45.7264 12.3989 46.4841 12.4069 47.2015C14.1885 44.2271 16.8245 42.1717 20.3956 40.6968C22.2013 39.8747 23.0798 42.9459 21.3144 43.5988C16.776 45.38 14.0674 48.4913 13.5271 53.0135C13.2046 54.3677 10.8024 54.69 10.4478 53.1745C9.2793 46.8144 8.07049 40.6236 6.902 34.2635C6.45875 32.3691 9.19144 31.41 9.80397 33.3447C10.5051 35.6582 11.037 37.9314 11.5689 40.2046C26.8373 20.4078 51.6494 8.608 76.6377 16.3478C104.165 24.6922 117.883 54.9697 119.196 81.7561C119.293 82.8524 117.688 82.8281 117.592 81.7318Z"
          fill="#4E7DA6"
        />
      </g>
      <defs>
        <clipPath id="arrow2-clip">
          <rect
            width="123.548"
            height="57.4236"
            fill="white"
            transform="translate(13.2891) rotate(13.3805)"
          />
        </clipPath>
      </defs>
    </svg>
  );
}

// Step 3 — bottom-left, curves up-right toward center
function Arrow3() {
  return (
    <svg height="72" viewBox="0 0 148 142" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clipPath="url(#arrow3-clip)">
        <path
          d="M137.574 87.3726C138.345 81.7969 139.945 76.446 142.467 70.9804C142.852 70.0426 142.309 69.0095 141.287 69.2536C134.135 70.9628 127.087 72.7525 119.934 74.4616C118.504 74.8035 118.159 77.2115 119.701 77.1603C123.706 76.9432 127.601 76.4355 131.577 75.1682C120.192 87.4032 105.478 98.3192 90.565 102.094C69.1185 107.641 66.6073 87.6269 56.8033 75.3808C44.4418 59.94 21.3018 56.3777 0.696243 66.27C-0.115366 66.6754 0.223321 67.7573 1.03493 67.352C15.3637 61.0738 30.4749 60.7402 42.4742 66.8588C48.7337 70.0146 53.9071 74.8042 57.5742 80.9053C61.4514 87.1675 63.9215 94.6117 68.9023 99.8702C85.9759 118.308 121.114 90.9885 136.359 73.2855L136.459 73.1561C137.276 72.9607 138.298 72.7166 139.115 72.5212C137.195 77.4203 135.787 82.3023 134.9 87.3773C134.742 89.1064 137.212 89.1505 137.574 87.3726Z"
          fill="#4E7DA6"
        />
      </g>
      <defs>
        <clipPath id="arrow3-clip">
          <rect
            width="121.59"
            height="84.6552"
            fill="white"
            transform="translate(51.5157) rotate(37.4837)"
          />
        </clipPath>
      </defs>
    </svg>
  );
}

// Step 4 — bottom-right, curves up-left toward center
function Arrow4() {
  return (
    <svg height="68" viewBox="0 0 135 101" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M0.202466 94.1074C1.94349 96.6782 3.87237 98.3219 6.40292 99.7171C7.53894 100.25 8.20681 98.2099 7.07077 97.6767C6.03792 97.1228 5.21142 96.5272 4.36753 95.8081C17.7218 100.229 32.3304 103.762 44.2864 92.9649C49.1822 88.5471 52.9745 82.3192 54.9596 75.3124C57.4235 66.4304 56.2232 57.1441 56.4162 47.9578C56.749 28.4527 67.2691 11.9745 82.2922 4.87867C99.2919 -3.25107 118.616 2.12534 133.666 13.3187C134.131 13.6061 134.56 12.8844 134.095 12.597C110.711 -5.04617 76.1861 -6.84832 60.0461 24.7385C56.8045 31.1095 54.9573 38.3425 54.6241 45.7784C54.1575 56.0362 55.8585 66.619 52.6908 76.5323C49.7284 85.642 43.1689 93.3174 35.3298 96.4232C25.3937 100.46 14.7589 97.2694 4.9496 93.9121C7.62991 91.8469 10.4655 90.1315 13.577 88.8686C14.574 88.4135 14.0722 86.3549 13.0752 86.8101C12.972 86.8309 12.8689 86.8517 12.8689 86.8517C8.62245 88.3435 4.6519 90.2878 0.630326 92.6236C0.0112783 92.7484 -0.193953 93.5521 0.202466 94.1074Z"
        fill="#4E7DA6"
      />
    </svg>
  );
}

export function HomeHowWeWorkSection({ content }: HomeHowWeWorkSectionProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [activeStep, setActiveStep] = useState(0);
  const activeStepRef = useRef(activeStep);
  const lockedRef = useRef(false);

  useEffect(() => {
    activeStepRef.current = activeStep;
  }, [activeStep]);

  // ponytail: wheel-driven stepper instead of scroll-distance scrubbing —
  // a scroll-linked opacity range gets blown past (or never reached) by a
  // single fast trackpad flick; capturing wheel input while the section is
  // pinned guarantees exactly one step advances per scroll gesture.
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    function onWheel(e: WheelEvent) {
      if (window.innerWidth < 1024) return; // mobile layout has no stepper

      const rect = section!.getBoundingClientRect();
      const pinned = rect.top <= 0 && rect.bottom >= window.innerHeight;
      if (!pinned) return;

      const forward = e.deltaY > 0;
      if (forward && activeStepRef.current >= 4) return;
      if (!forward && activeStepRef.current <= 0) return;

      e.preventDefault();
      if (lockedRef.current) return;
      lockedRef.current = true;
      setActiveStep((s) => Math.min(4, Math.max(0, s + (forward ? 1 : -1))));
      window.setTimeout(() => {
        lockedRef.current = false;
      }, 550);
    }

    window.addEventListener("wheel", onWheel, { passive: false });
    return () => window.removeEventListener("wheel", onWheel);
  }, []);

  const [step1, step2, step3, step4] = content.steps;

  return (
    <section ref={sectionRef} className="relative h-[160vh] bg-background">
      <div className="sticky top-0 flex h-screen flex-col items-center justify-center overflow-hidden">
        <div className="mx-auto flex w-full max-w-7xl flex-col items-center px-6 sm:px-10 lg:px-12">
          {/* heading */}
          <div className="pb-24 text-center">
            <h2 className="mt-4 font-serif text-4xl tracking-tight text-foreground md:text-5xl lg:text-6xl">
              {content.title}
            </h2>
          </div>

          {/* desktop three-column layout */}
          <div className="hidden w-full max-w-7xl lg:grid lg:grid-cols-[1fr_400px_1fr] lg:items-center lg:gap-12">
            {/* left column: steps 1 (top) & 3 (bottom) */}
            <div className="flex h-[500px] flex-col justify-between">
              <StepCard
                step={step1}
                number="01"
                arrowSide="right"
                visible={activeStep >= 1}
                ArrowEl={<Arrow1 />}
              />
              <StepCard
                step={step3}
                number="03"
                arrowSide="right"
                visible={activeStep >= 3}
                ArrowEl={<Arrow3 />}
              />
            </div>

            {/* center image — no container, no clipping */}
            <div className="mx-auto flex flex-shrink-0 items-center justify-center">
              <Image
                src={content.imageUrl}
                alt={content.imageAlt}
                width={400}
                height={600}
                className="h-auto w-full max-w-[400px] object-contain"
                priority
              />
            </div>

            {/* right column: steps 2 (top) & 4 (bottom) — cards pushed to right edge */}
            <div className="flex h-[500px] flex-col items-end justify-between">
              <StepCard
                step={step2}
                number="02"
                arrowSide="left"
                visible={activeStep >= 2}
                ArrowEl={<Arrow2 />}
              />
              <StepCard
                step={step4}
                number="04"
                arrowSide="left"
                visible={activeStep >= 4}
                ArrowEl={<Arrow4 />}
              />
            </div>
          </div>

          {/* mobile vertical layout */}
          <div className="flex w-full max-w-sm flex-col gap-6 lg:hidden">
            <div className="mx-auto flex items-center justify-center">
              <Image
                src={content.imageUrl}
                alt={content.imageAlt}
                width={200}
                height={300}
                className="h-auto w-full max-w-[200px] object-contain"
                priority
              />
            </div>
            {content.steps.map((step, i) => (
              <div key={step.id} className="flex gap-4">
                <span className="mt-0.5 shrink-0 text-xs font-bold tracking-widest text-primary/40">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <h3 className="font-serif text-lg text-foreground">{step.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

interface StepCardProps {
  step: HomeHowWeWorkStep;
  number: string;
  arrowSide: "left" | "right";
  visible: boolean;
  ArrowEl: React.ReactNode;
}

function StepCard({ step, number, arrowSide, visible, ArrowEl }: StepCardProps) {
  const textBlock = (
    <div className="flex flex-col gap-3">
      <span className="text-sm font-bold tracking-widest text-primary/40">{number}</span>
      <h3 className="font-serif text-3xl leading-snug text-foreground">{step.title}</h3>
      <p className="max-w-[260px] text-base leading-relaxed text-muted-foreground">
        {step.description}
      </p>
    </div>
  );

  return (
    <div
      className={cn(
        "flex items-center gap-4 transition-all duration-500 ease-out",
        visible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0",
      )}
    >
      {arrowSide === "right" ? (
        <>
          {textBlock}
          <div className="shrink-0">{ArrowEl}</div>
        </>
      ) : (
        <>
          {/* shift arrow left toward center image */}
          <div className="shrink-0 -translate-x-8">{ArrowEl}</div>
          {textBlock}
        </>
      )}
    </div>
  );
}
